/* eslint-disable lingui/no-unlocalized-strings -- caminhos HTTP, valores técnicos do fio e rótulos de log, não texto de UI */
import { z } from 'zod';

import type { Cents } from '@/shared/format/money';
import { stripDocumentSeparators } from '@/shared/format/document';
import { creditApi } from '@/shared/http';
import { wire, withSignal } from '@/shared/reference/wire';

import { paymentTransferAndCutoffDate } from './credit-note.form-values';
import { creditProductSchema } from './credit-note.wire';
import type { WarrantyItem } from './screens/tab-form';

// "Consultar › Consignação para compra" do Zetra (unidade 10H): ConsignmentContainer (Consignment.tsx) →
// SelectConsignmentContainer (os contratos passíveis de compra) → "Atualizar contratos" (UpdateConsignment.tsx, a
// consulta nova, paga). Cada chamada repete o legado (U02), com arquivo:linha. Nenhuma função aqui grava na
// operação: a tela escreve as garantias de portabilidade no formulário (`setValue`) e só o Salvar ou o "Incluir
// garantias" enviam. Só o ramo Zetra: no detalhe o "Consultar" só abre com produto Zetra (o SIAPE fica no "nova").

const text = z.string().nullish();
const num = z.number().nullish();

/** `IResumo` (CheckPurchaseContractsReadModel.ts:13-29): `valorParcela` em reais. */
const resumoSchema = z.looseObject({
  adeNumero: z.union([z.number(), z.string()]).nullish(),
  adeIdentificador: text,
  responsavel: text,
  servico: text,
  codVerba: text,
  dataReserva: text,
  valorParcela: num,
  prazo: num,
  pagas: num,
  situacao: text,
  servicoCodigo: text,
  statusCodigo: text,
  consignataria: text,
  consignatariaCodigo: text,
});
export type PurchaseResumo = z.infer<typeof resumoSchema>;

/** A autorização de portabilidade do SIAPE (`IVinculoFuncionalSummary.Produtos[].AutorizacaoPortabilidades`), centavos. */
const siapePortabilitySchema = z.looseObject({ Autorizado: text, DtValidade: text, VlMargemDisp: num, ContratoPortado: z.looseObject({ NrContrato: text }).nullish() });
const siapeProductSchema = z.looseObject({ CdRubrica: text, DsRubrica: text, CdConvenio: text, VlMargemDisp: num, AutorizacaoPortabilidades: z.array(siapePortabilitySchema).nullish() });
const siapeLinkSchema = z.looseObject({
  CdMatricula: text,
  CodOrgao: text,
  NmOrgao: text,
  CnpjOrgao: text,
  DescTipoVinc: text,
  CodSituacaoFuncional: text,
  DescSituacaoFuncional: text,
  Produtos: z.array(siapeProductSchema).nullish(),
});
export type SiapePurchaseLink = z.infer<typeof siapeLinkSchema>;

/** `CheckPurchaseContractsReadModel` (CheckPurchaseContractsReadModel.ts:3-12); no SIAPE, os `VinculoFuncionals` (R5). */
const purchaseContractsSchema = z.looseObject({
  sucesso: z.boolean().nullish(),
  codRetorno: text,
  mensagem: text,
  resumos: z.array(resumoSchema).nullish(),
  VinculoFuncionals: z.array(siapeLinkSchema).nullish(),
});
export type PurchaseContracts = z.infer<typeof purchaseContractsSchema>;

/** `TCheckConsignmentsResult[]` (CheckConsignmentsReadModel.ts:12-28): o legado lê o `result` do primeiro. */
const purchaseListSchema = z.array(z.looseObject({ createdAt: text, result: purchaseContractsSchema.nullish() }));

/** O `resource` do legado (Consignment.tsx:35-38): o caminho é `/{resource}/PurchaseMarginQuery`. */
export type PurchaseResource = 'Zetra' | 'Siape';

export interface PurchaseQueryParams {
  readonly registrationNumber: string;
  readonly employeeCode?: string | undefined;
  /** "Matrícula do Instituidor", só do SIAPE (MarginQueryForm.tsx:78-90). */
  readonly employeeInstCode?: string | undefined;
}

/**
 * Os contratos passíveis de compra guardados (`getCheckPurchaseContractsAsync`, zetra.ts:46-54, pelo
 * `useCheckPurchaseContracts` de contexts/zetra/index.ts:30-100 com `{ registrationNumber, employeeCode,
 * employeeInstCode }`): o `employeeInstCode` é só do SIAPE e o axios tira o `undefined`. Só leitura.
 */
export async function getPurchaseContracts(input: PurchaseQueryParams, signal?: AbortSignal, resource: PurchaseResource = 'Zetra'): Promise<PurchaseContracts | undefined> {
  const params: Record<string, string> = { registrationNumber: input.registrationNumber };
  if (input.employeeCode) params['employeeCode'] = input.employeeCode;
  if (input.employeeInstCode) params['employeeInstCode'] = input.employeeInstCode;
  const data = await creditApi.get<unknown>(`/${resource}/PurchaseMarginQuery`, { params, ...withSignal(signal) });
  if (!Array.isArray(data)) return undefined;
  // `data.map((x) => x.result)[0]` (contexts/zetra/index.ts:78).
  return wire(purchaseListSchema, data, `GET /${resource}/PurchaseMarginQuery`)[0]?.result ?? undefined;
}

export interface PurchaseRequest {
  readonly registrationNumber: string;
  readonly employeeCode: string | null;
  readonly employeePassword: string | null | undefined;
  readonly creditProductId: string | null | undefined;
  /** Só do SIAPE (UpdateConsignment.tsx:80-87). */
  readonly employeeInstCode?: string | null | undefined;
}

/**
 * **Consulta nova ao Zetra, paga** ("Atualizar contratos", `postConsignmentForPurchaseAsync`, zetra.ts:20-44, com o
 * payload de UpdateConsignment.tsx:69-90): corpo `{}` e tudo na query, nesta ordem. A senha vazia vai como o texto
 * `""` (`isEmpty(employeePassword) ? '""'`); `payrollAgreementCode` e `employeeInstCode` nunca chegam no Zetra e o
 * axios os tira. Prévia e clique (X01).
 */
export async function requestPurchaseContracts(input: PurchaseRequest, resource: PurchaseResource = 'Zetra'): Promise<unknown> {
  const params: Record<string, string> = { registrationNumber: input.registrationNumber };
  // No SIAPE a matrícula é opcional (`string().optional().nullable()`): `null` sai da query (axios).
  if (input.employeeCode !== null) params['employeeCode'] = input.employeeCode;
  params['employeePassword'] = input.employeePassword ? input.employeePassword : '""';
  // `watch('productId')`: `null`/`undefined` saem da query (axios); `''` iria vazio.
  if (input.creditProductId !== null && input.creditProductId !== undefined) params['creditProductId'] = input.creditProductId;
  // zetra.ts:33-40: `payrollAgreementCode` nunca chega; `employeeInstCode` só no SIAPE, `null` sai.
  if (input.employeeInstCode !== null && input.employeeInstCode !== undefined) params['employeeInstCode'] = input.employeeInstCode;
  return creditApi.post(`/${resource}/PurchaseMarginQuery`, {}, { params });
}

// ─── regras puras ─────────────────────────────────────────────────────────────────────────────────

/** A matrícula como o campo do legado a deixa (`replace(/\s/g, '')`, MarginQueryForm.tsx:72-74): só sem espaços. */
export const employeeCodeOf = (value: string | null | undefined) => (value ?? '').replace(/\s/g, '');

/** O CPF na query: só sem os separadores (o legado mandava `onlyNumbers` no `GET` e o valor mascarado no `POST`). */
export const purchaseDocument = (value: string) => stripDocumentSeparators(value);

/** O valor de cada cartão e da garantia (`${adeNumero}-${adeIdentificador}-${prazo}`, Summary/index.tsx:19). */
export const purchaseKey = (resumo: PurchaseResumo) => `${String(resumo.adeNumero ?? '')}-${resumo.adeIdentificador ?? ''}-${String(resumo.prazo ?? '')}`;

/** O que o "Confirmar" leva do formulário da escolha (SelectConsignmentContainer.tsx:72-84). */
export interface ConsignmentValues {
  readonly warrantyOption: string;
  /** O que está no campo "Dia do repasse" (o `string()` do yup converte o número do órgão em texto). */
  readonly paymentTransferDay: string;
  readonly employeeCode: string;
  /** O `internalId` do órgão escolhido. */
  readonly agreementCode: string | null | undefined;
  readonly warrantyRegistrationCutoffDay: number | null | undefined;
}

export interface ConsignmentWrite {
  readonly warranty: WarrantyItem[];
  readonly requestedAmount: Cents;
  /** Zetra: o maior prazo; SIAPE: o `numberOfPayments` que já estava no formulário (SelectConsignmentContainer.tsx:136). */
  readonly maxTermInMonths: unknown;
  readonly firstPaymentDate?: string;
}

/**
 * A escolha da Consignação para compra (SelectConsignmentContainer.tsx:95-127, 160-217, 266-305;
 * `mapConsignmentWarranty`, payrollAgreement/helpers:95-118). Comportamento efetivo do legado:
 * - as garantias atuais ficam, menos as de portabilidade quando elas já são `QUANTITY_CONSIGNMENT_SELECT` ou mais;
 * - cada contrato marcado vira uma garantia de portabilidade, menos o que já está nas atuais (`consignmentValue`),
 *   mesmo quando a atual saiu pelo limite;
 * - `warrantyDeadline` é o de agora (ou o da KOVR): o `warrantyDeadline` do formulário sai do `rest` antes do mapa;
 * - `governmentDepartmentCode` é `null` (o mapa lê o parâmetro, que ninguém passa) e o ADE anterior é o do contrato;
 * - o valor solicitado é a soma das parcelas em centavos e o prazo é o maior deles.
 * Nenhum contrato da lista marcado: `null` (o legado fecha sem escrever).
 */
export function consignmentWrite(input: {
  readonly contracts: PurchaseContracts | undefined;
  readonly keys: readonly string[];
  readonly values: ConsignmentValues;
  readonly registrationNumber: string;
  readonly current: readonly WarrantyItem[];
  readonly limit: number | undefined;
  readonly warrantyDeadline: string;
  readonly startDate: unknown;
}): ConsignmentWrite | null {
  const { values, current, limit } = input;
  const selected = (input.contracts?.resumos ?? []).filter((resumo) => input.keys.includes(purchaseKey(resumo)));
  if (selected.length === 0) return null;
  const maxTermInMonths = selected.reduce((max, resumo) => Math.max(max, resumo.prazo ?? 0), 0);
  const portability = current.filter((item) => item['warrantyOption'] === 'Portability').length;
  // `x >= undefined` é falso no legado: sem a chave, nenhuma sai.
  const kept = current.filter((item) => !(limit !== undefined && portability >= limit && item['warrantyOption'] === 'Portability'));
  const added: WarrantyItem[] = selected
    .filter((resumo) => !current.some((item) => item['consignmentValue'] === purchaseKey(resumo)))
    .map((resumo) => ({
      warrantyType: 'PublicPayroll',
      warrantyOption: values.warrantyOption,
      governmentDepartmentCode: null,
      paymentTransferDay: values.paymentTransferDay,
      employeeCode: values.employeeCode,
      // `resumo.valorParcela && Math.round(resumo.valorParcela * 100)`: 0, `null` e ausente passam como estão.
      paymentAmountInCents: resumo.valorParcela ? Math.round(resumo.valorParcela * 100) : resumo.valorParcela,
      warrantyDeadline: input.warrantyDeadline,
      previousAdeNumber: String(resumo.adeNumero ?? ''),
      simulationRegistrationNumber: input.registrationNumber,
      termInMonths: maxTermInMonths,
      consignmentValue: purchaseKey(resumo),
      agreementCode: values.agreementCode,
    }));
  // `reduce(... valorParcela ?? 0) * 100`, arredondado em centavos (o legado gravava o produto em ponto flutuante).
  const requestedAmount = Math.round(selected.reduce((sum, resumo) => sum + (resumo.valorParcela ?? 0), 0) * 100);
  // `if (warrantyRegistrationCutoffDay && paymentTransferDay)` com a data de início (SelectConsignmentContainer.tsx:288-298).
  const cutoff = values.warrantyRegistrationCutoffDay;
  const firstPaymentDate = cutoff && values.paymentTransferDay && typeof input.startDate === 'string' && input.startDate !== '' ? paymentTransferAndCutoffDate(input.startDate, Number(values.paymentTransferDay), cutoff) : undefined;
  return { warranty: [...kept, ...added], requestedAmount, maxTermInMonths, ...(firstPaymentDate ? { firstPaymentDate } : {}) };
}

/**
 * Os contratos já escolhidos (as garantias de portabilidade atuais) marcados ao abrir a lista (SelectConsignmentForm.tsx:99-111).
 * A portabilidade sem `consignmentValue` (a do "Margem") entra vazia: conta no "selecionados" e no limite, sem cartão.
 */
export function preselectedKeys(current: readonly WarrantyItem[]): string[] {
  return current.filter((item) => item['warrantyOption'] === 'Portability').map((item) => (typeof item['consignmentValue'] === 'string' ? item['consignmentValue'] : ''));
}

/** `QUANTITY_CONSIGNMENT_SELECT` (config.interface.ts:88): número, ou ausente (sem limite). */
export function consignmentLimit(config: Readonly<Record<string, unknown>>): number | undefined {
  const value = config['QUANTITY_CONSIGNMENT_SELECT'];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** O que a Consignação para compra lê do produto: os órgãos e a regra da senha (SelectConsignmentForm.tsx:87-95; Consignment.tsx:37-38). */
const consignmentProductSchema = creditProductSchema.pick({ governmentDepartments: true, payrollAgreement: true });
export type ConsignmentProduct = z.infer<typeof consignmentProductSchema>;

/** O produto de outro módulo (a Simulação) no formato que a Consignação lê; fora do formato, sem órgãos nem regra. */
export function consignmentProduct(raw: unknown): ConsignmentProduct | undefined {
  const parsed = consignmentProductSchema.safeParse(raw);
  return parsed.success ? parsed.data : undefined;
}

// ─── SIAPE (R5: Consignment.tsx com `resource='Siape'`, SummaryPortability, SelectConsignmentContainer.tsx:131-159) ──

/** Um cartão do SIAPE (`summaryPortability`, SummaryPortability/index.tsx:6-62): cada autorização `S` de cada produto. */
export interface SiapePortabilityOption {
  readonly key: string;
  readonly contract: string;
  readonly link: SiapePurchaseLink;
  readonly product: z.infer<typeof siapeProductSchema>;
  readonly validity: string;
  /** Centavos. */
  readonly value: number;
}

export function siapePortabilityOptions(contracts: PurchaseContracts | undefined): SiapePortabilityOption[] {
  return (contracts?.VinculoFuncionals ?? []).flatMap((link) =>
    (link.Produtos ?? []).flatMap((product) =>
      (product.AutorizacaoPortabilidades ?? [])
        .filter((authorization) => authorization.Autorizado === 'S')
        .map((authorization) => ({
          // `${CdMatricula}-${NrContrato}` (o `undefined` vira texto, como o template do legado).
          key: `${String(link.CdMatricula)}-${String(authorization.ContratoPortado?.NrContrato)}`,
          contract: authorization.ContratoPortado?.NrContrato ?? 'N/A',
          link,
          product,
          validity: authorization.DtValidade ?? 'N/A',
          value: authorization.VlMargemDisp ?? 0,
        })),
    ),
  );
}

/**
 * A escolha do SIAPE (`processSiapeFlow` + `mapWarrantyModelsSiape` + `updateAmortizationValues`,
 * SelectConsignmentContainer.tsx:131-159, 206-253, 255-305; `transformPurchaseContracts`, SummaryPortability:64-115):
 * - os contratos vêm só do **primeiro produto** de cada vínculo (o cartão de outro produto não acha contrato);
 * - as atuais ficam, menos as portabilidades no limite; cada contrato novo vira portabilidade com a margem da
 *   autorização (ou a do produto, ou 0), o prazo de averbação do formulário, o código do órgão, o convênio e a situação;
 * - o valor é a soma das margens (centavos); o prazo e as parcelas ficam com o `numberOfPayments` que estava.
 * Nenhum contrato achado: `null` (o legado fecha sem escrever).
 */
export function siapeConsignmentWrite(input: {
  readonly contracts: PurchaseContracts | undefined;
  readonly keys: readonly string[];
  readonly registrationNumber: string;
  readonly current: readonly WarrantyItem[];
  readonly limit: number | undefined;
  readonly warrantyDeadline: string | null;
  readonly employmentStatusCode: string | null | undefined;
  readonly employmentStatusDescription: string | null | undefined;
  readonly numberOfPayments: unknown;
}): ConsignmentWrite | null {
  const { current, limit } = input;
  const transformed = (input.contracts?.VinculoFuncionals ?? []).flatMap((link) => {
    const product = link.Produtos?.[0];
    return (product?.AutorizacaoPortabilidades ?? [])
      .filter((authorization) => authorization.Autorizado === 'S')
      .map((authorization) => ({
        employeeCode: link.CdMatricula,
        agreementCode: product?.CdConvenio,
        governmentDepartmentCode: link.CodOrgao,
        paymentAmountInCents: authorization.VlMargemDisp || product?.VlMargemDisp || 0,
        contract: authorization.ContratoPortado?.NrContrato,
        validity: authorization.DtValidade,
      }));
  });
  const selected = transformed.filter((item) => input.keys.includes(`${String(item.employeeCode)}-${String(item.contract)}`));
  if (selected.length === 0) return null;
  const portability = current.filter((item) => item['warrantyOption'] === 'Portability').length;
  const kept = current.filter((item) => !(limit !== undefined && portability >= limit && item['warrantyOption'] === 'Portability'));
  const added: WarrantyItem[] = selected
    .filter((item) => !current.some((warranty) => warranty['consignmentValue'] === `${String(item.employeeCode)}-${String(item.contract)}`))
    .map((item) => ({
      warrantyType: 'PublicPayroll',
      warrantyOption: 'Portability',
      governmentDepartmentCode: item.governmentDepartmentCode ?? null,
      employeeCode: item.employeeCode,
      paymentAmountInCents: item.paymentAmountInCents,
      warrantyDeadline: input.warrantyDeadline,
      previousAdeNumber: item.contract ?? null,
      simulationRegistrationNumber: input.registrationNumber,
      termInMonths: item.validity ? 10 : 0,
      consignmentValue: `${String(item.employeeCode)}-${String(item.contract)}`,
      agreementCode: item.agreementCode ?? null,
      employmentStatusCode: input.employmentStatusCode ?? null,
      employmentStatusDescription: input.employmentStatusDescription ?? null,
    }));
  const requestedAmount = selected.reduce((sum, item) => sum + item.paymentAmountInCents, 0);
  return { warranty: [...kept, ...added], requestedAmount, maxTermInMonths: input.numberOfPayments };
}
